import React from 'react';
import { useMutation } from '@apollo/client';
import useInterval from '@hooks/useInterval';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { useUserActivityContext } from '@context/UserActivityContext';
import { useAuthExpired } from '@/apollo/authExpired';
import { HeartbeatResponse } from '@graphql/gql/graphql';
import { HEARTBEAT } from '@graphql/operations/user/heartBeat';

export const LOG = LoggerFactory.create('UserActivityHeartbeat');

// if user is inactive for more than 15 minutes stop sending heartbeats
export const USER_IDLE_THRESHOLD = 15 * 60 * 1000;
// we only want to request refreshing token every 5 minutes
export const EXTEND_SESSION_FREQUENCY_SECONDS = 5 * 60;

// check if user is active based on the last activity
const isUserActive = lastActive => {
  const elapsedTimeSinceLastActive = Date.now() - lastActive;
  return elapsedTimeSinceLastActive < USER_IDLE_THRESHOLD;
};

const UserActivityHeartbeat = ({
  heartbeatFrequencyInSeconds = 60
}: {
  heartbeatFrequencyInSeconds?: number;
}): null => {
  const [heartbeat] = useMutation<{ heartbeatV2: HeartbeatResponse }>(HEARTBEAT);
  const { authExpired, setAuthExpired } = useAuthExpired();
  const { state: userActivityState, setBoothStaffAvailabilityHeartbeatStatus } = useUserActivityContext();

  const [sessionLastExtendedTime, setSessionLastExtendedTime] = React.useState(Date.now());

  // we only send heartbeat mutation on set intervals while the user is active
  useInterval(async () => {
    if (authExpired) {
      LOG.debug('Auth token expired. Skipping heartbeat');
      return;
    }

    if (!isUserActive(userActivityState.lastActive)) {
      LOG.debug('User inactive. Last activity', {
        ...userActivityState,
        lastActive: new Date(userActivityState.lastActive)
      });
      return;
    }

    let extendSession = false;
    // calculate the time elapsed since last time we extended the session/auth token
    const elapsedTimeSinceLastRun = Math.ceil((Date.now() - sessionLastExtendedTime) / 1000);

    if (elapsedTimeSinceLastRun >= EXTEND_SESSION_FREQUENCY_SECONDS) {
      extendSession = true;
      setSessionLastExtendedTime(Date.now());
    }

    const variables = {
      variables: {
        heartbeatInput: {
          extendSession,
          sendBoothStaffAvailabilityHeartbeat: userActivityState.extendBoothStaffAvailability,
          videoActivity: userActivityState.videoActivity
        }
      }
    };

    LOG.debug('Sending heartbeat mutation', variables);
    const response = await heartbeat({
      variables: {
        heartbeatInput: {
          extendSession,
          sendBoothStaffAvailabilityHeartbeat: userActivityState.extendBoothStaffAvailability,
          videoActivity: userActivityState.videoActivity
        }
      }
    });

    if (response.errors) {
      LOG.error('Error trying to send heartbeat mutation', response.errors);
      return;
    }
    const {
      sessionExtendedStatus,
      encounterPublished,
      boothStaffAvailabilityHeartbeatStatus: boothStaffAvailabilityHeartbeatStatusResponse
    } = response.data.heartbeatV2;

    // The value for sessionExtendedStatus will be null only if we didn't request session/auth token to be extended
    if (sessionExtendedStatus === 401 || sessionExtendedStatus === 404) {
      // token expired
      LOG.info(`Unauthenticated user has been logged out. HTTP Status: ${sessionExtendedStatus}`);
      setAuthExpired(true);
    } else if (sessionExtendedStatus !== null && sessionExtendedStatus !== 204) {
      LOG.error(`Unable to extend bearer token. HTTP Status: ${sessionExtendedStatus}`);
    }
    if (encounterPublished === false) {
      LOG.error('Unable to publish the encounter heartbeat');
    }
    setBoothStaffAvailabilityHeartbeatStatus(boothStaffAvailabilityHeartbeatStatusResponse);
  }, heartbeatFrequencyInSeconds * 1000);

  return null;
};
export default UserActivityHeartbeat;
